import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import adminApi from "../../api/adminApi";
import { clearAppSettingsCache } from "../../../shared utils/appSettings";

const errorText = (error, fallback) => {
  const b = error?.body;
  if (b && typeof b === "object") return b.message || b.error || fallback;
  if (typeof b === "string" && b.trim()) return b;
  return error?.message || fallback;
};

// The hubspot.* settings tune the deal importer, which runs on at most ONE
// backend (a config var decides which). On a backend where it's off they
// change nothing, so they're shown but not editable. Given the status
// endpoint's answer, returns the set of keys to lock; empty when the import
// is on, or when the backend is too old to say (null status).
export function inertSettingKeys(status, rows = []) {
  if (!status || status.enabled) return new Set();
  const listed = Array.isArray(status.settingKeys) ? status.settingKeys : [];
  const keys = listed.length
    ? listed
    : rows.map((r) => r.key).filter((k) => String(k).startsWith("hubspot."));
  return new Set(keys);
}

// Platform-wide settings: a plain key / description / value table. Keys come
// from migrations (each with its description), so there's no add or delete
// here — just the value. The first two are the accepted student age range.
export default function AppSettingsTable({ setBanner }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState(false); // endpoint not deployed here
  const [hubspotImport, setHubspotImport] = useState(null); // null = backend can't say
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setMissing(false);
    try {
      const [data, status] = await Promise.all([
        adminApi.getSettings(),
        // Older backends have no status endpoint: treat as "can't say" and
        // leave every row editable, as before.
        adminApi.getHubspotImportStatus().catch(() => null),
      ]);
      setRows(Array.isArray(data) ? data : []);
      setHubspotImport(status && typeof status === "object" ? status : null);
    } catch (error) {
      if (error?.status === 404) setMissing(true);
      else setBanner({ type: "error", text: errorText(error, "Failed to load settings") });
    } finally {
      setLoading(false);
    }
  }, [setBanner]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (row) => {
    setEditKey(row.key);
    setDraft(row.value ?? "");
  };

  const save = async () => {
    if (editKey == null || saving) return;
    setSaving(true);
    try {
      await adminApi.updateSetting(editKey, draft.trim());
      clearAppSettingsCache(); // so the next screen that reads settings sees it
      setEditKey(null);
      await load();
      setBanner({ type: "success", text: `Saved ${editKey}` });
    } catch (error) {
      setBanner({ type: "error", text: errorText(error, `Failed to save ${editKey}`) });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !rows.length) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (missing) {
    return (
      <Typography variant="body2" sx={{ opacity: 0.75 }}>
        This backend doesn&apos;t have settings yet (GET api/settings is 404). Until it does,
        nothing that depends on a setting — such as the student age-range warning — is shown.
      </Typography>
    );
  }

  const inert = inertSettingKeys(hubspotImport, rows);
  const hasHubspotRows = rows.some((r) => String(r.key).startsWith("hubspot."));

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" sx={{ opacity: 0.75 }}>
        Values the rest of the app reads rather than has built in. Keys are added by the
        backend with a description; only the value is editable here.
      </Typography>
      {hasHubspotRows && hubspotImport && !hubspotImport.enabled && (
        <Alert severity="info">
          The HubSpot deal import is switched off on this server, so the <code>hubspot.*</code>{" "}
          settings below have no effect here and can&apos;t be edited. They&apos;re set on the server that runs the import.
        </Alert>
      )}
      {hasHubspotRows && hubspotImport?.enabled && hubspotImport.dryRun && (
        <Alert severity="warning">
          The HubSpot deal import is running in dry-run mode on this server: it polls on the schedule below but
          creates nothing.
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>What it does</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const editing = editKey === row.key;
              const locked = inert.has(row.key);
              return (
                <TableRow
                  key={row.key}
                  hover
                  sx={editing ? { bgcolor: "action.hover" } : locked ? { opacity: 0.6 } : undefined}
                >
                  <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                    {row.key}
                    {locked && (
                      <Chip size="small" label="import off here" sx={{ ml: 1, fontFamily: "inherit" }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>{row.description || "—"}</TableCell>
                  <TableCell>
                    {editing ? (
                      <TextField
                        size="small"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") save();
                          if (e.key === "Escape") setEditKey(null);
                        }}
                        autoFocus
                        sx={{ width: 160 }}
                      />
                    ) : (
                      <Typography component="span" sx={{ fontWeight: 600 }}>
                        {row.value}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {row.updatedAt ? new Date(row.updatedAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                      {row.updatedBy ? ` · ${row.updatedBy}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {editing ? (
                      <>
                        <Button size="small" onClick={() => setEditKey(null)} disabled={saving}>
                          Cancel
                        </Button>
                        <Button size="small" variant="contained" onClick={save} disabled={saving}>
                          {saving ? "Saving…" : "Save"}
                        </Button>
                      </>
                    ) : locked ? (
                      <Tooltip title="HubSpot import is off on this server — this setting has no effect here">
                        <span>
                          <IconButton size="small" aria-label={`Edit ${row.key}`} disabled>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <IconButton size="small" aria-label={`Edit ${row.key}`} disabled={editKey != null} onClick={() => startEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No settings
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
